import React, { useState, useEffect } from 'react';
import { Phone, Mail, Star, UserCheck, Clock, FileText, Award, ChevronRight, X, ChevronLeft, Paintbrush, Droplets } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Slider } from './ui/slider';
import { services, references, usps, processSteps, teamMembers, contactInfo } from '../mock';

const iconMap = {
  'user-check': UserCheck,
  'clock': Clock,
  'file-text': FileText,
  'award': Award
};

// Farbpalette aus dem Bild - Hauptfarbe #659dc4
const colorPalette = [
  { name: 'blue', class: 'from-blue-400 to-blue-500', solid: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500', hex: '#659dc4' },
  { name: 'teal', class: 'from-teal-400 to-teal-500', solid: 'bg-teal-500', text: 'text-teal-500', border: 'border-teal-500', hex: '#80cbc4' },
  { name: 'green', class: 'from-green-400 to-green-500', solid: 'bg-green-500', text: 'text-green-500', border: 'border-green-500', hex: '#a5d6a7' },
  { name: 'yellow', class: 'from-yellow-400 to-yellow-500', solid: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500', hex: '#fff59d' },
  { name: 'orange', class: 'from-orange-400 to-orange-500', solid: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500', hex: '#ffcc80' },
  { name: 'red', class: 'from-red-400 to-red-500', solid: 'bg-red-500', text: 'text-red-500', border: 'border-red-500', hex: '#ef9a9a' }
];

const Home = () => {
  const [activeFilter, setActiveFilter] = useState('alle');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    rueckmeldung: '',
    anzahlRaeume: '',
    wohnflaeche: '',
    dringlichkeit: [5],
    realisationDatum: '',
    realisationArbeitstage: [],
    projektarten: [],
    beschreibung: '',
    uploadedFiles: []
  });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredReferences = activeFilter === 'alle' 
    ? references 
    : references.filter(ref => ref.category === activeFilter);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxImage(filteredReferences[index]);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % filteredReferences.length;
    setCurrentImageIndex(nextIndex);
    setLightboxImage(filteredReferences[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentImageIndex - 1 + filteredReferences.length) % filteredReferences.length;
    setCurrentImageIndex(prevIndex);
    setLightboxImage(filteredReferences[prevIndex]);
  };

  const handleCheckboxChange = (value) => {
    setFormData(prev => ({
      ...prev,
      projektarten: prev.projektarten.includes(value)
        ? prev.projektarten.filter(item => item !== value)
        : [...prev.projektarten, value]
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentFiles = formData.uploadedFiles;
    
    // Max 10 Dateien
    if (currentFiles.length + files.length > 10) {
      alert('Maximal 10 Dateien erlaubt');
      return;
    }
    
    // Validierung: Max 10MB pro Datei
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`${file.name} ist zu groß (max 10 MB)`);
        return false;
      }
      return true;
    });
    
    // Dateien mit Preview hinzufügen
    const newFiles = validFiles.map(file => ({
      file,
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setFormData(prev => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...newFiles]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Formular gesendet:', formData);
    alert('Vielen Dank für Ihre Anfrage! Wir melden uns in Kürze bei Ihnen.');
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Paint Splatters Background */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-10">
        <div className="paint-splatter paint-splatter-red" style={{width: '200px', height: '200px', top: '10%', left: '5%', animation: 'splatter 3s ease-in-out infinite'}}></div>
        <div className="paint-splatter paint-splatter-darkred" style={{width: '150px', height: '150px', top: '30%', right: '10%', animation: 'splatter 4s ease-in-out infinite', animationDelay: '0.5s'}}></div>
        <div className="paint-splatter paint-splatter-black" style={{width: '180px', height: '180px', bottom: '20%', left: '15%', animation: 'splatter 3.5s ease-in-out infinite', animationDelay: '1s'}}></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-40 border-b-4 border-blue-400" style={{borderBottomColor: '#659dc4'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <a href="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_regenbogen-maler/artifacts/c6ymnj7c_Black%20Red%20Bold%20Minimalist%20Racing%20Gear%20Logo%20%281%29.png" 
                  alt="Maler Labbiento Logo" 
                  className="h-20"
                />
              </a>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="/#home" className="text-gray-700 hover:text-blue-500 transition-colors font-medium brush-underline" style={{color: '#659dc4'}}>Home</a>
              <a href="/#leistungen" className="text-gray-700 hover:text-teal-500 transition-colors font-medium brush-underline">Leistungen</a>
              <a href="/farbvisualisierung.html" className="text-gray-700 hover:text-green-500 transition-colors font-medium brush-underline flex items-center gap-1">
                <Paintbrush className="w-4 h-4" />
                Farbvisualisierung
              </a>
              <a href="/#referenzen" className="text-gray-700 hover:text-orange-500 transition-colors font-medium brush-underline">Referenzen</a>
              <a href="/ueber-uns.html" className="text-gray-700 hover:text-red-400 transition-colors font-medium brush-underline">Über uns</a>
              <a href="/partner.html" className="text-gray-700 hover:text-green-400 transition-colors font-medium brush-underline">Partner</a>
              <a href="/kontakt.html" className="text-gray-700 hover:text-blue-500 transition-colors font-medium brush-underline">Kontakt</a>
            </nav>
            <div className="hidden md:block">
              <Button className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white" style={{background: 'linear-gradient(135deg, #659dc4 0%, #4a7a9d 100%)'}}>
                <Phone className="w-4 h-4 mr-2" />
                Jetzt anrufen
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Floating Contact Sticker - Desktop */}
      <div className="hidden lg:block fixed right-6 top-1/2 -translate-y-1/2 z-50">
        <div className="bg-white rounded-lg shadow-2xl p-4 space-y-3">
          <a 
            href="tel:+41795202936" 
            className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white rounded-full transition-all hover:scale-110 shadow-lg"
            title="Anrufen"
          >
            <Phone className="w-6 h-6" />
          </a>
          <a 
            href="mailto:info@maler-labbiento.ch" 
            className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-full transition-all hover:scale-110 shadow-lg"
            style={{background: 'linear-gradient(135deg, #659dc4 0%, #4a7a9d 100%)'}}
            title="E-Mail senden"
          >
            <Mail className="w-6 h-6" />
          </a>
        </div>
      </div>

      {/* Mobile Contact Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
        <div className="flex">
          <a 
            href="tel:+41795202936" 
            className="flex-1 flex items-center justify-center py-4 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white transition-colors"
          >
            <Phone className="w-5 h-5 mr-2" />
            <span className="font-medium">Anrufen</span>
          </a>
          <a 
            href="mailto:info@maler-labbiento.ch" 
            className="flex-1 flex items-center justify-center py-4 text-white transition-colors"
            style={{background: 'linear-gradient(90deg, #659dc4 0%, #4a7a9d 100%)'}}
          >
            <Mail className="w-5 h-5 mr-2" />
            <span className="font-medium">E-Mail</span>
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-teal-50 to-yellow-50 opacity-70"></div>
        
        {/* Animated paint drips */}
        <div className="absolute top-0 left-0 w-full h-20 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-10 w-2 h-full bg-gradient-to-b from-blue-400 to-transparent animate-paintDrip" style={{backgroundColor: '#659dc4'}}></div>
          <div className="absolute top-0 left-1/4 w-2 h-full bg-gradient-to-b from-teal-400 to-transparent animate-paintDrip" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute top-0 left-2/4 w-2 h-full bg-gradient-to-b from-green-400 to-transparent animate-paintDrip" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-0 left-3/4 w-2 h-full bg-gradient-to-b from-yellow-400 to-transparent animate-paintDrip" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute top-0 right-20 w-2 h-full bg-gradient-to-b from-orange-400 to-transparent animate-paintDrip" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Paintbrush className="w-8 h-8" style={{color: '#659dc4'}} />
                <span className="inline-flex">
                  <span className="w-3 h-3 rounded-full mx-1" style={{backgroundColor: '#659dc4'}}></span>
                  <span className="w-3 h-3 rounded-full bg-teal-400 mx-1"></span>
                  <span className="w-3 h-3 rounded-full bg-green-400 mx-1"></span>
                  <span className="w-3 h-3 rounded-full bg-yellow-400 mx-1"></span>
                  <span className="w-3 h-3 rounded-full bg-orange-400 mx-1"></span>
                  <span className="w-3 h-3 rounded-full bg-red-400 mx-1"></span>
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight animate-fadeIn">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #80cbc4 50%, #a5d6a7 100%)'}}>Malergeschäft</span>{' '}
                <span className="text-gray-900">Labbiento GmbH</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 font-medium animate-fadeIn" style={{animationDelay: '0.1s'}}>
                Ihr Maler in Lenzburg und Region Aargau
              </p>
              
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start animate-fadeIn paint-fill rounded-lg p-2 -ml-2" style={{animationDelay: '0.2s'}}>
                  <ChevronRight className="w-5 h-5 mr-2 mt-1 flex-shrink-0 animate-pulse" style={{color: '#659dc4'}} />
                  <span>Innenbereich – Wände, Decken, Treppenhäuser</span>
                </div>
                <div className="flex items-start animate-fadeIn paint-fill rounded-lg p-2 -ml-2" style={{animationDelay: '0.3s'}}>
                  <ChevronRight className="w-5 h-5 mr-2 mt-1 text-teal-400 flex-shrink-0 animate-pulse" style={{animationDelay: '0.2s'}} />
                  <span>Aussenbereich – Fassaden, Fenster, Untersichten</span>
                </div>
                <div className="flex items-start animate-fadeIn paint-fill rounded-lg p-2 -ml-2" style={{animationDelay: '0.4s'}}>
                  <ChevronRight className="w-5 h-5 mr-2 mt-1 text-green-400 flex-shrink-0 animate-pulse" style={{animationDelay: '0.4s'}} />
                  <span>Stucco Veneziano – Edle Glanzoptik</span>
                </div>
                <div className="flex items-start animate-fadeIn paint-fill rounded-lg p-2 -ml-2" style={{animationDelay: '0.5s'}}>
                  <ChevronRight className="w-5 h-5 mr-2 mt-1 text-yellow-400 flex-shrink-0 animate-pulse" style={{animationDelay: '0.6s'}} />
                  <span>Farbberatung – Individuelle Konzepte</span>
                </div>
                <div className="flex items-start animate-fadeIn paint-fill rounded-lg p-2 -ml-2" style={{animationDelay: '0.6s'}}>
                  <ChevronRight className="w-5 h-5 mr-2 mt-1 text-orange-400 flex-shrink-0 animate-pulse" style={{animationDelay: '0.8s'}} />
                  <span>Ausbesserungsarbeiten – Gipser & Reparaturen</span>
                </div>
              </div>

              <div className="flex items-center space-x-1 text-yellow-500 animate-fadeIn" style={{animationDelay: '0.7s'}}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-current" />
                ))}
                <span className="ml-3 text-gray-700 font-medium">5.0 / 5.0 – 24+ Google-Bewertungen</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fadeIn" style={{animationDelay: '0.8s'}}>
                <a href="#kontakt">
                  <Button size="lg" className="text-white text-lg px-8 py-6 hover:scale-105 transition-transform shadow-lg relative overflow-hidden group" style={{background: 'linear-gradient(135deg, #659dc4 0%, #80cbc4 100%)', ':hover': {background: 'linear-gradient(135deg, #4a7a9d 0%, #5fa89f 100%)'}}}>
                    <span className="relative z-10 flex items-center">
                      <Droplets className="w-5 h-5 mr-2" />
                      Unverbindliche Offerte anfordern
                    </span>
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative animate-fadeIn" style={{animationDelay: '0.4s'}}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500 border-4" style={{borderColor: '#659dc4'}}>
                <img 
                  src="https://images.unsplash.com/photo-1688372199140-cade7ae820fe" 
                  alt="Professioneller Maler bei der Arbeit" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -top-8 -right-8 w-24 h-24 opacity-50">
                <Paintbrush className="w-full h-full transform rotate-45" style={{color: '#659dc4'}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #80cbc4 100%)'}}>Warum</span> Maler Labbiento?
            </h2>
            <p className="text-xl text-gray-600">Qualität, Zuverlässigkeit und Erfahrung – darauf können Sie sich verlassen</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {usps.map((usp, index) => {
              const IconComponent = iconMap[usp.icon];
              const color = colorPalette[index % colorPalette.length];
              return (
                <Card key={usp.id} className={`border-2 hover:shadow-2xl transition-all group relative overflow-hidden`} style={{borderColor: color.hex}}>
                  <div className={`absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300`} style={{background: `linear-gradient(135deg, ${color.hex}30 0%, ${color.hex}60 100%)`}}></div>
                  <CardHeader className="relative z-10">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`} style={{background: `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 100%)`}}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{usp.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <CardDescription className="text-base">{usp.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - OHNE horizontale Linie */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              So läuft ein <span className="text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #80cbc4 100%)'}}>Auftrag</span> bei uns ab
            </h2>
            <p className="text-xl text-gray-600">Transparent, klar und professionell von Anfang bis Ende</p>
          </div>
          
          <div className="relative">
            {/* KEINE horizontale Linie mehr */}
            <div className="grid md:grid-cols-5 gap-8 relative z-10">
              {processSteps.map((step, index) => {
                const color = colorPalette[index % colorPalette.length];
                return (
                  <div 
                    key={step.id} 
                    className="text-center transition-all duration-300"
                    style={{
                      transform: hoveredStep === index ? 'scale(1.15)' : 'scale(1)',
                      zIndex: hoveredStep === index ? 20 : 10
                    }}
                    onMouseEnter={() => setHoveredStep(index)}
                    onMouseLeave={() => setHoveredStep(null)}
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 text-white rounded-full text-2xl font-bold mb-4 shadow-lg transition-all duration-300 ${
                      hoveredStep === index ? 'ring-4 ring-offset-2 shadow-2xl' : ''
                    }`} style={{background: `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 100%)`, ringColor: hoveredStep === index ? color.hex : 'transparent'}}>
                      {index + 1}
                    </div>
                    <h3 className={`text-lg font-bold mb-2 transition-colors`} style={{color: hoveredStep === index ? color.hex : '#111827'}}>{step.title}</h3>
                    <p className={`text-gray-600 transition-all duration-300 ${
                      hoveredStep === index ? 'text-base' : 'text-sm'
                    }`}>{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="leistungen" className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere <span className="text-transparent bg-clip-text" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #a5d6a7 100%)'}}>Leistungen</span>
            </h2>
            <p className="text-xl text-gray-600">Professionelle Malerarbeiten für jeden Bedarf</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const color = colorPalette[index % colorPalette.length];
              return (
                <Card key={service.id} className={`overflow-hidden hover:shadow-2xl transition-all group border-2`} style={{borderColor: color.hex}}>
                  <div className="h-64 overflow-hidden relative">
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 z-10`} style={{background: `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}aa 100%)`}}></div>
                    <img 
                      src={service.image} 
                      alt={service.alt} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className={`text-2xl transition-colors`}>{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base mb-4">{service.description}</CardDescription>
                    <Button className={`w-full text-white transition-all`} style={{background: `linear-gradient(135deg, ${color.hex} 0%, ${color.hex}dd 100%)`}}>
                      Mehr erfahren
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Action Button zur Offerteanfrage */}
          <div className="text-center mt-16">
            <a href="#kontakt">
              <Button size="lg" className="text-white text-xl px-12 py-8 hover:scale-105 transition-all shadow-2xl" style={{background: 'linear-gradient(135deg, #659dc4 0%, #80cbc4 100%)'}}>
                <span className="flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Jetzt kostenlose Offerte anfragen
                </span>
              </Button>
            </a>
            <p className="text-gray-600 mt-4 text-lg">Unverbindlich • Kostenlos • In 24h Rückmeldung</p>
          </div>
        </div>
      </section>

      {/* References Section */}
      <section id="referenzen" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere <span className="text-transparent bg-clip-text" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #fff59d 100%)'}}>Referenzen</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">Überzeugen Sie sich von unserer Arbeit</p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant={activeFilter === 'alle' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('alle')}
                className={activeFilter === 'alle' ? 'text-white' : ''}
                style={activeFilter === 'alle' ? {background: 'linear-gradient(135deg, #659dc4 0%, #80cbc4 100%)'} : {borderColor: '#659dc4'}}
              >
                Alle
              </Button>
              <Button 
                variant={activeFilter === 'innen' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('innen')}
                className={activeFilter === 'innen' ? 'text-white' : ''}
                style={activeFilter === 'innen' ? {background: 'linear-gradient(135deg, #80cbc4 0%, #a5d6a7 100%)'} : {borderColor: '#80cbc4'}}
              >
                Innenbereich
              </Button>
              <Button 
                variant={activeFilter === 'aussen' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('aussen')}
                className={activeFilter === 'aussen' ? 'text-white' : ''}
                style={activeFilter === 'aussen' ? {background: 'linear-gradient(135deg, #a5d6a7 0%, #fff59d 100%)'} : {borderColor: '#a5d6a7'}}
              >
                Aussenbereich
              </Button>
              <Button 
                variant={activeFilter === 'stucco' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('stucco')}
                className={activeFilter === 'stucco' ? 'text-white' : ''}
                style={activeFilter === 'stucco' ? {background: 'linear-gradient(135deg, #fff59d 0%, #ffcc80 100%)'} : {borderColor: '#fff59d'}}
              >
                Stucco Veneziano
              </Button>
              <Button 
                variant={activeFilter === 'gipser' ? 'default' : 'outline'}
                onClick={() => setActiveFilter('gipser')}
                className={activeFilter === 'gipser' ? 'text-white' : ''}
                style={activeFilter === 'gipser' ? {background: 'linear-gradient(135deg, #ffcc80 0%, #ef9a9a 100%)'} : {borderColor: '#ffcc80'}}
              >
                Gipserarbeiten
              </Button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredReferences.map((ref, index) => {
              const color = colorPalette[index % colorPalette.length];
              return (
                <div 
                  key={ref.id} 
                  className={`relative group cursor-pointer rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all border-4`}
                  style={{borderColor: color.hex}}
                  onClick={() => openLightbox(index)}
                >
                  <img 
                    src={ref.image} 
                    alt={ref.alt} 
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-60 transition-opacity flex items-center justify-center`} style={{background: `linear-gradient(135deg, ${color.hex}aa 0%, ${color.hex}dd 100%)`}}>
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-lg font-bold">Ansehen</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button 
            className="absolute top-4 right-4 text-white hover:text-blue-400 transition-colors"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-blue-400 transition-colors"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft className="w-12 h-12" />
          </button>
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-blue-400 transition-colors"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight className="w-12 h-12" />
          </button>
          <img 
            src={lightboxImage.image} 
            alt={lightboxImage.alt} 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section für Kontakt */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white relative">
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Bereit für Ihr <span className="text-transparent bg-clip-text" style={{backgroundImage: 'linear-gradient(90deg, #659dc4 0%, #80cbc4 100%)'}}>nächstes Projekt</span>?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns für eine unverbindliche Offerte oder besuchen Sie unsere Kontaktseite!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/kontakt.html">
              <Button size="lg" className="text-white text-lg px-8 py-6 hover:scale-105 transition-transform shadow-lg" style={{background: 'linear-gradient(135deg, #659dc4 0%, #80cbc4 100%)'}}>
                Zur Kontaktseite
              </Button>
            </a>
            <a href="tel:+41795202936">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 hover:scale-105 transition-transform" style={{borderColor: '#659dc4', color: '#659dc4'}}>
                <Phone className="w-5 h-5 mr-2" />
                Jetzt anrufen
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Colorful top border */}
        <div className="absolute top-0 left-0 w-full h-1" style={{background: 'linear-gradient(90deg, #659dc4 0%, #80cbc4 20%, #a5d6a7 40%, #fff59d 60%, #ffcc80 80%, #ef9a9a 100%)'}}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_regenbogen-maler/artifacts/c6ymnj7c_Black%20Red%20Bold%20Minimalist%20Racing%20Gear%20Logo%20%281%29.png" 
                alt="Maler Labbiento Logo" 
                className="h-16 mb-4 brightness-0 invert"
              />
              <p className="text-gray-400">
                Malergeschäft Labbiento GmbH<br />
                Kronenplatz 12<br />
                5600 Lenzburg
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4" style={{color: '#659dc4'}}>Kontakt</h3>
              <div className="space-y-2 text-gray-400">
                <p>Mobile: {contactInfo.mobile}</p>
                <p>E-Mail: {contactInfo.email}</p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4 text-gray-400">Einsatzgebiet</h3>
              <p className="text-gray-400">
                Lenzburg, Kanton Aargau und umliegende Gemeinden
              </p>
              <div className="mt-6 space-y-2 text-sm text-gray-500">
                <a href="#impressum" className="block transition-colors" style={{':hover': {color: '#659dc4'}}}>Impressum</a>
                <a href="#datenschutz" className="block transition-colors" style={{':hover': {color: '#659dc4'}}}>Datenschutz</a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>© 2025 Malergeschäft Labbiento GmbH. Alle Rechte vorbehalten.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;